import { Segment } from './Segment.js';
import { SegmentClones } from './SegmentClones.js';
import { keyframes, styled } from '@/stitches/index.js';
import { useWindowSize } from '@contra/react-hooks/useWindowSize';
import { type CSS } from '@stitches/react';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';

const translationKeyframes = keyframes({
  '0%': { transform: 'translateX(0%) translateZ(0px)' },
  '100%': { transform: 'translateX(-100%) translateZ(0px)' },
});

const MarqueeWrapper = styled('div', {
  overflowX: 'hidden',
  transform: 'translate3d(0,0,0)',
  whiteSpace: 'nowrap',
  width: '100vw',
  zIndex: 1,
});

export const MarqueeTransformContainer = styled('div', {
  animation: `${translationKeyframes} 60s infinite linear`,
  display: 'inline-block',
  overflowX: 'hidden',
  variants: {
    reverse: {
      true: {
        animationDirection: 'reverse',
      },
    },
  },
  width: 'max-content',
  zIndex: 1,
});

type MarqueeProps = {
  readonly children: ReactNode;
  readonly className?: string;
  readonly css?: CSS;
  readonly duration?: string;
  readonly itemSpacingValue?: number | string;
  /**
   * Animates the marquee to the opposite direction instead
   */
  readonly reverse?: boolean;
};
export const Marquee = ({
  children,
  className,
  css,
  duration,
  itemSpacingValue,
  reverse = false,
}: MarqueeProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [segmentCloneCount, setSegmentCloneCount] = useState<number>(0);
  const { width: windowWidth } = useWindowSize();
  const durationOverride = useMemo(
    () => (duration ? { animationDuration: duration } : null),
    [duration],
  );

  // Calculate segmentCloneCount
  useEffect(() => {
    const segmentWidth = ref.current ? ref.current.offsetWidth : 0;

    setSegmentCloneCount(
      Math.ceil(windowWidth ? windowWidth / segmentWidth : 0),
    );
  }, [windowWidth]);

  return (
    <MarqueeWrapper
      // eslint-disable-next-line react/forbid-component-props
      className={className}
      css={css}
    >
      {Array.from({ length: 2 }).map((_, index) => (
        <MarqueeTransformContainer
          css={{
            ...durationOverride,
          }}
          // eslint-disable-next-line react/no-array-index-key
          key={`marquee-${index}`}
          reverse={reverse}
        >
          <Segment
            itemSpacingValue={itemSpacingValue}
            ref={index === 0 ? ref : undefined}
          >
            {children}
          </Segment>
          <SegmentClones
            itemSpacingValue={itemSpacingValue}
            segmentCloneCount={segmentCloneCount}
          >
            {children}
          </SegmentClones>
        </MarqueeTransformContainer>
      ))}
    </MarqueeWrapper>
  );
};
